<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import DriveAPI from 'dashboard/api/drive';
import RagAPI from 'dashboard/api/rag';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const files = ref([]);
const allFiles = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const selectedFiles = ref([]); // Fichiers sélectionnés pour vectorisation
const vectorizationStatus = ref('idle'); // idle, processing, completed
const vectorizationProgress = ref(0);
const categories = ref([]);
const categoriesLoading = ref(false);
const selectedCategory = ref(null);
const categoryFiles = ref([]);
const searchResults = ref([]);
const searchFilter = ref('all'); // all, documents, images, videos
const uploadingFiles = ref(new Set()); // Track des fichiers en cours d'upload

const totalFiles = computed(() => files.value.length);

const vectorizedFiles = computed(() => {
  return selectedFiles.value.filter(f => f.vectorized).length;
});

const totalSelectedFiles = computed(() => selectedFiles.value.length);

const statsCards = computed(() => [
  {
    metric: t('DRIVE.RAG.TOTAL_FILES'),
    value: totalFiles.value.toString(),
    subtext: t('DRIVE.RAG.AVAILABLE_FOR_RAG'),
    icon: 'i-lucide-files',
    iconColor: 'text-n-slate-11',
  },
  {
    metric: t('DRIVE.RAG.VECTORIZED_FILES'),
    value: vectorizedFiles.value.toString(),
    subtext: `${totalSelectedFiles.value > 0 ? ((vectorizedFiles.value / totalSelectedFiles.value) * 100).toFixed(0) : 0}% ${t('DRIVE.RAG.COMPLETED')}`,
    icon: 'i-lucide-database',
    iconColor: 'text-n-blue-11',
    iconBg: 'bg-n-blue-3',
  },
  {
    metric: 'Catégories',
    value: categories.value.length.toString(),
    subtext: 'catégories disponibles',
    icon: 'i-lucide-tag',
    iconColor: 'text-n-purple-11',
    iconBg: 'bg-n-purple-3',
  },
]);

const toggleFile = file => {
  const index = selectedFiles.value.findIndex(f => f.id === file.id);
  if (index > -1) {
    selectedFiles.value.splice(index, 1);
  } else {
    selectedFiles.value.push({ ...file, vectorized: false });
  }
};

const selectAllFiles = () => {
  selectedFiles.value = files.value.map(f => ({ ...f, vectorized: false }));
};

const deselectAllFiles = () => {
  selectedFiles.value = [];
};

const startVectorization = async () => {
  if (selectedFiles.value.length === 0) {
    useAlert('Veuillez sélectionner au moins un fichier');
    return;
  }

  if (!currentUser.value?.id) {
    useAlert('Utilisateur non identifié');
    return;
  }

  // Convertir l'ID user en string pour le header UUID
  const userId = String(currentUser.value.id);

  // eslint-disable-next-line no-console
  console.log('User ID pour Railway:', userId, 'Type:', typeof userId);

  vectorizationStatus.value = 'processing';
  let successCount = 0;
  let failCount = 0;

  for (const fileData of selectedFiles.value) {
    try {
      uploadingFiles.value.add(fileData.id);

      // Récupérer les infos complètes du fichier
      const driveFile = allFiles.value.find(f => f.id === fileData.id);
      if (!driveFile) {
        throw new Error('Fichier introuvable');
      }

      // La réponse peut avoir file_url, share_url ou url
      const fileUrl =
        driveFile.file_url || driveFile.share_url || driveFile.url;
      if (!fileUrl) {
        throw new Error(
          "URL du fichier non disponible - le fichier n'est peut-être pas attaché"
        );
      }

      // Télécharger le fichier en tant que blob via fetch
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(
          `Échec du téléchargement: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();

      // Créer un objet File depuis le blob
      const file = new File([blob], driveFile.name, {
        type: driveFile.content_type || blob.type || 'application/octet-stream',
      });

      // Uploader vers le webhook Railway avec l'ID en string
      await RagAPI.uploadFile(file, userId);

      // Marquer comme vectorisé
      const fileIndex = selectedFiles.value.findIndex(
        f => f.id === fileData.id
      );
      if (fileIndex > -1) {
        selectedFiles.value[fileIndex].vectorized = true;
      }

      successCount += 1;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Erreur vectorisation ${fileData.name}:`, error);
      failCount += 1;
    } finally {
      uploadingFiles.value.delete(fileData.id);
    }
  }

  vectorizationStatus.value = 'completed';

  if (successCount > 0) {
    useAlert(`${successCount} fichier(s) vectorisé(s) avec succès`);
    // Recharger les catégories après vectorisation
    await loadCategories();
  }

  if (failCount > 0) {
    useAlert(`${failCount} fichier(s) en échec`);
  }
};

const searchByKeyword = async () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = [];
    return;
  }

  if (!currentUser.value?.id) {
    useAlert('Utilisateur non identifié');
    return;
  }

  const userId = String(currentUser.value.id);
  try {
    const response = await RagAPI.searchByKeyword(searchQuery.value, userId);
    searchResults.value = response.data.files || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erreur recherche:', error);
    useAlert('Erreur lors de la recherche');
    searchResults.value = [];
  }
};

const loadCategories = async () => {
  if (!currentUser.value?.id) return;

  const userId = String(currentUser.value.id);
  categoriesLoading.value = true;
  try {
    const response = await RagAPI.getAllCategories(userId);
    categories.value = response.data.categories || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erreur chargement catégories:', error);
    useAlert('Erreur lors du chargement des catégories');
  } finally {
    categoriesLoading.value = false;
  }
};

const loadFilesByCategory = async categoryName => {
  if (!currentUser.value?.id) return;

  const userId = String(currentUser.value.id);
  try {
    const response = await RagAPI.getFilesByCategory(categoryName, userId);
    categoryFiles.value = response.data.files || [];
    selectedCategory.value = categoryName;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erreur chargement fichiers catégorie:', error);
    useAlert('Erreur lors du chargement des fichiers');
  }
};

const fetchFiles = async () => {
  loading.value = true;
  try {
    const response = await DriveAPI.getAll({});
    allFiles.value = response.data;
    files.value = response.data.filter(f => f.name !== '.folder');
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchFiles();
  await loadCategories();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header class="sticky top-0 z-10">
      <div
        class="flex items-center w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
      >
        <span class="text-xl font-medium text-n-slate-12">
          {{ t('DRIVE.RAG.TITLE') }}
        </span>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-[60rem] mx-auto py-4 space-y-6">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex items-center justify-center py-20 text-n-slate-11"
        >
          <Spinner />
        </div>

        <template v-else>
          <!-- Overview Cards -->
          <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div
              v-for="stat in statsCards"
              :key="stat.metric"
              class="group relative overflow-hidden rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm transition-all duration-300 hover:shadow-md hover:border-n-blue-6"
            >
              <div class="p-6">
                <div class="flex items-center justify-between">
                  <div class="space-y-3 flex-1">
                    <p class="text-sm font-medium text-n-slate-11">
                      {{ stat.metric }}
                    </p>
                    <div class="space-y-1">
                      <h3
                        class="text-3xl font-bold tracking-tight text-n-slate-12 transition-colors group-hover:text-n-brand"
                      >
                        {{ stat.value }}
                      </h3>
                      <p class="text-xs text-n-slate-10">
                        {{ stat.subtext }}
                      </p>
                    </div>
                  </div>
                  <div
                    v-if="stat.iconBg"
                    class="flex-shrink-0 rounded-full p-3 transition-transform group-hover:scale-110"
                    :class="stat.iconBg"
                  >
                    <span
                      class="size-6 transition-transform"
                      :class="[stat.icon, stat.iconColor]"
                    />
                  </div>
                  <span
                    v-else
                    class="size-8 transition-transform group-hover:scale-110"
                    :class="[stat.icon, stat.iconColor]"
                  />
                </div>
              </div>
              <div
                class="absolute inset-0 bg-gradient-to-br from-n-brand/0 to-n-brand/0 group-hover:from-n-brand/5 group-hover:to-transparent transition-all duration-300 pointer-events-none"
              />
            </div>
          </div>

          <!-- Vectorization Configuration -->
          <div
            class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
          >
            <div class="p-6 border-b border-n-weak">
              <h3 class="text-lg font-semibold text-n-slate-12">
                Sélection des fichiers à vectoriser
              </h3>
              <p class="text-sm text-n-slate-10 mt-1">
                Sélectionnez les fichiers du Drive à vectoriser dans la base RAG
              </p>
            </div>
            <div class="p-6 space-y-6">
              <!-- File Selection -->
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <label class="text-sm font-medium text-n-slate-12">
                    Fichiers disponibles
                  </label>
                  <div class="flex gap-2">
                    <button
                      class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
                      @click="selectAllFiles"
                    >
                      {{ t('DRIVE.RAG.SELECT_ALL') }}
                    </button>
                    <span class="text-n-slate-10">|</span>
                    <button
                      class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
                      @click="deselectAllFiles"
                    >
                      {{ t('DRIVE.RAG.DESELECT_ALL') }}
                    </button>
                  </div>
                </div>

                <div
                  class="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto p-3 rounded-lg border border-n-weak bg-n-alpha-1"
                >
                  <label
                    v-for="file in files"
                    :key="file.id"
                    class="flex items-center gap-3 p-3 rounded-md hover:bg-n-alpha-2 cursor-pointer transition-colors border border-transparent hover:border-n-blue-6"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedFiles.some(f => f.id === file.id)"
                      class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                      @change="toggleFile(file)"
                    />
                    <span
                      class="size-5 flex-shrink-0"
                      :class="file.icon_class || 'i-lucide-file'"
                    />
                    <div class="flex-1 min-w-0">
                      <span class="text-sm text-n-slate-12 truncate block">
                        {{ file.name }}
                      </span>
                      <span class="text-xs text-n-slate-10">
                        {{ file.human_file_size }}
                      </span>
                    </div>
                    <span
                      v-if="
                        selectedFiles.find(f => f.id === file.id)?.vectorized
                      "
                      class="i-lucide-check-circle size-4 text-n-green-11"
                    />
                    <span
                      v-else-if="uploadingFiles.has(file.id)"
                      class="i-lucide-loader-2 size-4 text-n-blue-11 animate-spin"
                    />
                  </label>
                  <div
                    v-if="files.length === 0"
                    class="text-center py-8 text-sm text-n-slate-10"
                  >
                    Aucun fichier disponible dans le Drive
                  </div>
                </div>

                <div class="flex items-center gap-2 text-xs text-n-slate-10">
                  <span class="i-lucide-info size-4" />
                  <span>
                    {{ selectedFiles.length }} fichier(s) sélectionné(s) •
                    {{ vectorizedFiles }} vectorisé(s)
                  </span>
                </div>
              </div>

              <!-- Action Button -->
              <div class="flex justify-end">
                <Button
                  :disabled="
                    vectorizationStatus === 'processing' ||
                    selectedFiles.length === 0
                  "
                  @click="startVectorization"
                >
                  <span
                    v-if="vectorizationStatus === 'processing'"
                    class="i-lucide-loader-2 size-4 animate-spin"
                  />
                  <span v-else class="i-lucide-upload size-4" />
                  {{
                    vectorizationStatus === 'processing'
                      ? 'Vectorisation en cours...'
                      : 'Vectoriser les fichiers'
                  }}
                </Button>
              </div>
            </div>
          </div>

          <!-- Vectorization Progress -->
          <div
            v-if="vectorizationStatus !== 'idle'"
            class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
          >
            <div class="p-6 border-b border-n-weak">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12">
                    {{ t('DRIVE.RAG.VECTORIZATION_PROGRESS') }}
                  </h3>
                  <p class="text-sm text-n-slate-10 mt-1">
                    {{ t('DRIVE.RAG.VECTORIZATION_PROGRESS_DESC') }}
                  </p>
                </div>
                <div
                  v-if="vectorizationStatus === 'processing'"
                  class="flex items-center gap-2"
                >
                  <Spinner />
                  <span class="text-sm text-n-slate-11">
                    {{ t('DRIVE.RAG.PROCESSING') }}
                  </span>
                </div>
                <div
                  v-else
                  class="flex items-center gap-2 px-3 py-1 rounded-full bg-n-green-3"
                >
                  <span class="i-lucide-check-circle size-4 text-n-green-11" />
                  <span class="text-sm font-medium text-n-green-11">
                    {{ t('DRIVE.RAG.COMPLETED') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="p-6">
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-n-slate-11">
                    {{ t('DRIVE.RAG.FILES_PROCESSED') }}
                  </span>
                  <span class="text-sm font-bold text-n-slate-12">
                    {{ vectorizedFiles }} / {{ totalFiles }}
                  </span>
                </div>
                <div
                  class="relative w-full bg-n-slate-3 rounded-full h-4 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full bg-gradient-to-r from-n-brand to-n-blue-11 transition-all duration-500"
                    :style="{ width: `${vectorizationProgress}%` }"
                  />
                  <div
                    class="absolute inset-0 flex items-center justify-center text-xs font-medium text-n-slate-12"
                  >
                    {{ vectorizationProgress.toFixed(0) }}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Catégories RAG -->
          <div v-if="categories.length > 0" class="space-y-4">
            <!-- Liste des catégories -->
            <div
              class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
            >
              <div class="p-6 border-b border-n-weak">
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-lg font-semibold text-n-slate-12">
                      Catégories RAG
                    </h3>
                    <p class="text-sm text-n-slate-10 mt-1">
                      Cliquez sur une catégorie pour voir les fichiers associés
                    </p>
                  </div>
                  <div class="rounded-full bg-n-purple-3 px-3 py-1">
                    <span class="text-xs font-medium text-n-purple-11">
                      {{ categories.length }} catégories
                    </span>
                  </div>
                </div>
              </div>
              <div class="p-6">
                <div
                  class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                >
                  <button
                    v-for="category in categories"
                    :key="category"
                    class="group relative flex flex-col items-center gap-2 p-4 rounded-lg border transition-all hover:shadow-md"
                    :class="
                      selectedCategory === category
                        ? 'border-n-brand bg-n-brand/5'
                        : 'border-n-weak bg-n-alpha-1 hover:border-n-blue-6'
                    "
                    @click="loadFilesByCategory(category)"
                  >
                    <span
                      class="i-lucide-folder size-8 transition-colors"
                      :class="
                        selectedCategory === category
                          ? 'text-n-brand'
                          : 'text-n-blue-10 group-hover:text-n-brand'
                      "
                    />
                    <span
                      class="text-sm font-medium text-center transition-colors"
                      :class="
                        selectedCategory === category
                          ? 'text-n-brand'
                          : 'text-n-slate-12 group-hover:text-n-brand'
                      "
                    >
                      {{ category }}
                    </span>
                    <span
                      v-if="selectedCategory === category"
                      class="absolute top-2 right-2 i-lucide-check-circle size-4 text-n-brand"
                    />
                  </button>
                </div>
              </div>
            </div>

            <!-- Fichiers de la catégorie sélectionnée -->
            <div
              v-if="selectedCategory && categoryFiles.length > 0"
              class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
            >
              <div class="p-6 border-b border-n-weak">
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-lg font-semibold text-n-slate-12">
                      Fichiers : {{ selectedCategory }}
                    </h3>
                    <p class="text-sm text-n-slate-10 mt-1">
                      {{ categoryFiles.length }} fichier(s) dans cette catégorie
                    </p>
                  </div>
                  <button
                    class="px-3 py-1 rounded-md bg-n-alpha-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-3 transition-colors"
                    @click="
                      selectedCategory = null;
                      categoryFiles = [];
                    "
                  >
                    <span class="i-lucide-x size-3" />
                    Fermer
                  </button>
                </div>
              </div>
              <div class="p-6">
                <div class="space-y-3">
                  <div
                    v-for="file in categoryFiles"
                    :key="file.file_name"
                    class="group flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-all"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="flex items-center gap-3 flex-1 min-w-0">
                        <span
                          class="i-lucide-file-text size-5 text-n-blue-10 flex-shrink-0"
                        />
                        <div class="flex-1 min-w-0">
                          <p
                            class="text-sm font-semibold text-n-slate-12 truncate"
                          >
                            {{ file.file_name }}
                          </p>
                          <p class="text-xs text-n-slate-10 mt-0.5">
                            {{
                              new Date(file.createdat).toLocaleDateString(
                                'fr-FR'
                              )
                            }}
                          </p>
                        </div>
                      </div>
                      <span
                        class="px-2 py-1 rounded-md bg-n-purple-3 text-xs font-medium text-n-purple-11"
                      >
                        {{ file.categorie }}
                      </span>
                    </div>

                    <!-- Informations supplémentaires si disponibles -->
                    <div
                      v-if="file.total_facture || file.resumer"
                      class="mt-2 pt-2 border-t border-n-weak space-y-1"
                    >
                      <p
                        v-if="file.total_facture"
                        class="text-xs text-n-slate-11"
                      >
                        <span class="font-medium">Total:</span>
                        {{ file.total_facture }}€
                      </p>
                      <p v-if="file.resumer" class="text-xs text-n-slate-11">
                        <span class="font-medium">Résumé:</span>
                        {{ file.resumer }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Enhanced Keyword Search -->
          <div
            class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
          >
            <div class="p-6 border-b border-n-weak">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12">
                    {{ t('DRIVE.RAG.SEARCH_BY_KEYWORDS') }}
                  </h3>
                  <p class="text-sm text-n-slate-10 mt-1">
                    {{ t('DRIVE.RAG.SEARCH_BY_KEYWORDS_DESC') }}
                  </p>
                </div>
                <div
                  v-if="searchResults.length > 0"
                  class="rounded-full bg-n-green-3 px-3 py-1"
                >
                  <span class="text-xs font-medium text-n-green-11">
                    {{ searchResults.length }}
                    {{ t('DRIVE.RAG.RESULTS_FOUND') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="p-6 space-y-4">
              <!-- Search Input with Filters -->
              <div class="space-y-3">
                <div class="flex gap-2">
                  <Input
                    :model-value="searchQuery"
                    type="search"
                    :placeholder="t('DRIVE.RAG.SEARCH_PLACEHOLDER')"
                    class="flex-1"
                    @input="searchQuery = $event.target.value"
                    @keyup.enter="searchByKeyword"
                  >
                    <template #prefix>
                      <span class="i-lucide-search size-4 text-n-slate-10" />
                    </template>
                  </Input>
                  <Button @click="searchByKeyword">
                    <span class="i-lucide-sparkles size-4" />
                    {{ t('DRIVE.RAG.SEARCH') }}
                  </Button>
                </div>

                <!-- Filter Buttons -->
                <div class="flex items-center gap-2">
                  <span class="text-xs text-n-slate-10">
                    {{ t('DRIVE.RAG.FILTER_BY') }}:
                  </span>
                  <button
                    class="px-3 py-1 rounded-md text-xs font-medium transition-all"
                    :class="
                      searchFilter === 'all'
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                    "
                    @click="
                      searchFilter = 'all';
                      searchByKeyword();
                    "
                  >
                    {{ t('DRIVE.RAG.ALL') }}
                  </button>
                  <button
                    class="px-3 py-1 rounded-md text-xs font-medium transition-all"
                    :class="
                      searchFilter === 'document'
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                    "
                    @click="
                      searchFilter = 'document';
                      searchByKeyword();
                    "
                  >
                    <span class="i-lucide-file-text size-3" />
                    {{ t('DRIVE.RAG.DOCUMENTS') }}
                  </button>
                  <button
                    class="px-3 py-1 rounded-md text-xs font-medium transition-all"
                    :class="
                      searchFilter === 'image'
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                    "
                    @click="
                      searchFilter = 'image';
                      searchByKeyword();
                    "
                  >
                    <span class="i-lucide-image size-3" />
                    {{ t('DRIVE.RAG.IMAGES') }}
                  </button>
                  <button
                    class="px-3 py-1 rounded-md text-xs font-medium transition-all"
                    :class="
                      searchFilter === 'video'
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                    "
                    @click="
                      searchFilter = 'video';
                      searchByKeyword();
                    "
                  >
                    <span class="i-lucide-video size-3" />
                    {{ t('DRIVE.RAG.VIDEOS') }}
                  </button>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0" class="space-y-3">
                <div
                  class="divide-y divide-n-weak rounded-lg border border-n-weak"
                >
                  <div
                    v-for="result in searchResults"
                    :key="result.id"
                    class="group flex items-center gap-3 p-4 hover:bg-n-blue-2 dark:hover:bg-n-blue-3 transition-all cursor-pointer"
                  >
                    <div
                      class="flex items-center justify-center size-10 rounded-lg bg-n-alpha-2 group-hover:bg-n-blue-3 transition-colors"
                    >
                      <span
                        class="size-5 text-n-slate-10 group-hover:text-n-blue-11 transition-colors"
                        :class="[result.icon_class]"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-n-slate-12 truncate">
                        {{ result.name }}
                      </p>
                      <div class="flex items-center gap-2 mt-1">
                        <span class="i-lucide-folder size-3 text-n-slate-10" />
                        <p class="text-xs text-n-slate-10 truncate">
                          {{ result.folder_path || '/' }}
                        </p>
                      </div>
                    </div>
                    <div class="flex flex-col items-end gap-1">
                      <span class="text-xs font-medium text-n-slate-11">
                        {{ result.human_file_size }}
                      </span>
                      <span
                        class="px-2 py-0.5 rounded-md bg-n-alpha-2 text-[10px] font-medium text-n-slate-10 capitalize"
                      >
                        {{ result.file_type }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div
                v-else-if="searchQuery"
                class="flex flex-col items-center justify-center py-12 text-center"
              >
                <span class="i-lucide-search-x size-12 text-n-slate-10 mb-3" />
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('DRIVE.RAG.NO_RESULTS') }}
                </p>
                <p class="text-xs text-n-slate-10 mt-1">
                  {{ t('DRIVE.RAG.TRY_DIFFERENT_KEYWORDS') }}
                </p>
              </div>

              <!-- Empty State -->
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 text-center"
              >
                <span class="i-lucide-sparkles size-12 text-n-slate-10 mb-3" />
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('DRIVE.RAG.START_SEARCHING') }}
                </p>
                <p class="text-xs text-n-slate-10 mt-1">
                  {{ t('DRIVE.RAG.START_SEARCHING_DESC') }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </main>
  </section>
</template>
